// components/admin/ImportExportTab.js
import React, { useState } from "react"
import { importMaterials, addProduct, fetchProducts } from "../../api"
import Papa from "papaparse"

const ImportExportTab = ({ products, setProducts, setLoading }) => {
  const [activeSection, setActiveSection] = useState("materials")

  // Materials Import State
  const [materialsJson, setMaterialsJson] = useState("")
  const [materialsPreview, setMaterialsPreview] = useState(null)

  // Products Import State
  const [csvFile, setCsvFile] = useState(null)
  const [csvData, setCsvData] = useState(null)
  const [csvHeaders, setCsvHeaders] = useState([])
  const [fieldMapping, setFieldMapping] = useState({
    name: "",
    code: "",
    category: "",
    family: "",
    price: "",
    tag: "",
  })
  const [defaultValues, setDefaultValues] = useState({
    category: "",
    family: "",
    price: 0,
    tag: "",
  })

  const getAPIBaseURL = () => {
    return process.env.NODE_ENV === "production"
      ? "https://kosteaz.com/api"
      : "/api-proxy"
  }

  // Import materiali da JSON Emersya
  const handleMaterialsJsonChange = (e) => {
    const jsonText = e.target.value
    setMaterialsJson(jsonText)

    try {
      const parsed = JSON.parse(jsonText)
      setMaterialsPreview(parsed)
    } catch (error) {
      setMaterialsPreview(null)
    }
  }

  const importMaterialsFromJson = async () => {
    if (!materialsPreview) {
      alert("JSON non valido")
      return
    }

    setLoading(true)
    try {
      const result = await importMaterials(materialsPreview)
      alert(
        `Importati ${result.stats.imported} materiali, aggiornati ${result.stats.updated}`
      )
      setMaterialsJson("")
      setMaterialsPreview(null)
    } catch (error) {
      console.error("Errore import materiali:", error)
      alert(`Errore: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Gestione CSV per prodotti
  const handleCsvFile = (e) => {
    const file = e.target.files[0]
    if (!file) return

    setCsvFile(file)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.data && results.data.length > 0) {
          setCsvData(results.data)
          setCsvHeaders(Object.keys(results.data[0]))
        }
      },
      error: (error) => {
        console.error("Errore parsing CSV:", error)
        alert("Errore nel parsing del CSV")
      },
    })
  }

  const handleFieldMappingChange = (field, value) => {
    setFieldMapping((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleDefaultValueChange = (field, value) => {
    setDefaultValues((prev) => ({
      ...prev,
      [field]: field === "price" ? parseFloat(value) || 0 : value,
    }))
  }

  const importProductsFromCsv = async () => {
    if (!csvData || !fieldMapping.name || !fieldMapping.code) {
      alert("Devi mappare almeno Nome e Codice prodotto")
      return
    }

    setLoading(true)
    let imported = 0
    let errors = 0

    try {
      for (const row of csvData) {
        try {
          const productData = {
            name: row[fieldMapping.name] || "",
            code: row[fieldMapping.code] || "",
            category:
              row[fieldMapping.category] ||
              defaultValues.category ||
              "Senza Categoria",
            family:
              row[fieldMapping.family] ||
              defaultValues.family ||
              "Senza Famiglia",
            price:
              parseFloat(row[fieldMapping.price]) || defaultValues.price || 0,
            tag: row[fieldMapping.tag] || defaultValues.tag || "",
            slug: "",
            projectCode: "",
            materials: {},
            typeLinks: [],
            customViews: [],
          }

          if (productData.name && productData.code) {
            await addProduct(productData)
            imported++
          }
        } catch (error) {
          console.error("Errore importando prodotto:", row, error)
          errors++
        }
      }

      alert(`Importati ${imported} prodotti. Errori: ${errors}`)

      // Reset form
      setCsvFile(null)
      setCsvData(null)
      setCsvHeaders([])
      setFieldMapping({
        name: "",
        code: "",
        category: "",
        family: "",
        price: "",
        tag: "",
      })
    } catch (error) {
      console.error("Errore import prodotti:", error)
      alert(`Errore: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const exportCompleteProductsJSON = async () => {
    try {
      const API_BASE_URL = getAPIBaseURL()

      // Fetch dell'intero products.json dal server
      const response = await fetch(`${API_BASE_URL}/products.php`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const completeData = await response.json()

      const dataToExport = {
        products: completeData,
        exported_at: new Date().toISOString(),
        version: "1.0",
        export_type: "complete_backup",
        export_info: {
          categories_count: Object.keys(completeData.categories || {}).length,
          total_size_bytes: JSON.stringify(completeData).length,
        },
      }

      const blob = new Blob([JSON.stringify(dataToExport, null, 2)], {
        type: "application/json",
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `products-complete-backup-${
        new Date().toISOString().split("T")[0]
      }.json`
      link.click()
      URL.revokeObjectURL(url)

      console.log("Complete backup exported successfully")
    } catch (error) {
      console.error("Failed to export complete products:", error)
      alert("Errore durante l'export completo: " + error.message)
    }
  }

  // Export prodotti in CSV
  const exportProductsToCSV = () => {
    const csvData = products.map((product) => ({
      name: product.name,
      code: product.code,
      category: product._category || product.category,
      family: product._family || product.family,
      price: product.price || 0,
      tag: product.tag || "",
      slug: product.slug || "",
      projectCode: product.projectCode || "",
    }))

    const csv = Papa.unparse(csvData)
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute(
      "download",
      `products_export_${new Date().toISOString().split("T")[0]}.csv`
    )
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div>
      <h2 className="ay-text-2xl ay-font-bold ay-mb-6">Import & Export</h2>

      {/* Section Tabs */}
      <div className="ay-flex ay-mb-6 ay-bg-gray-100 ay-rounded-lg ay-p-1">
        <button
          className={`ay-px-4 ay-py-2 ay-rounded-md ay-font-medium ${
            activeSection === "materials"
              ? "ay-bg-white ay-shadow-sm"
              : "ay-text-gray-600 ay-hover:ay-text-gray-800"
          }`}
          onClick={() => setActiveSection("materials")}
        >
          🎨 Import Materiali
        </button>
        <button
          className={`ay-px-4 ay-py-2 ay-rounded-md ay-font-medium ${
            activeSection === "products"
              ? "ay-bg-white ay-shadow-sm"
              : "ay-text-gray-600 ay-hover:ay-text-gray-800"
          }`}
          onClick={() => setActiveSection("products")}
        >
          📦 Import Prodotti
        </button>
        <button
          className={`ay-px-4 ay-py-2 ay-rounded-md ay-font-medium ${
            activeSection === "export"
              ? "ay-bg-white ay-shadow-sm"
              : "ay-text-gray-600 ay-hover:ay-text-gray-800"
          }`}
          onClick={() => setActiveSection("export")}
        >
          📤 Export
        </button>
      </div>

      {/* Materials Import */}
      {activeSection === "materials" && (
        <div className="ay-space-y-6">
          <div className="ay-bg-blue-50 ay-p-4 ay-rounded-lg">
            <h3 className="ay-font-medium ay-mb-2">
              Import Materiali da JSON Emersya
            </h3>
            <p className="ay-text-sm ay-text-gray-600">
              Incolla qui il JSON esportato da Emersya con la lista dei
              materiali per tipo tecnico.
            </p>
          </div>

          <div>
            <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
              JSON Materiali Emersya
            </label>
            <textarea
              value={materialsJson}
              onChange={handleMaterialsJsonChange}
              className="ay-w-full ay-h-64 ay-p-3 ay-border ay-border-gray-300 ay-rounded-md ay-font-mono ay-text-sm"
              placeholder={`{
  "struttura": [
    "V1 Pure white",
    "V38 Signal Black",
    "V50 Ochre brown"
  ],
  "ripiani": [
    "ML1 Pure white",
    "MWL1 Natural oak"
  ]
}`}
            />
          </div>

          {materialsPreview && (
            <div className="ay-bg-green-50 ay-p-4 ay-rounded-lg">
              <h4 className="ay-font-medium ay-mb-2">Anteprima Import</h4>
              <div className="ay-space-y-2">
                {Object.entries(materialsPreview).map(([type, materials]) => (
                  <div key={type} className="ay-text-sm">
                    <span className="ay-font-medium">{type}:</span>{" "}
                    {materials.length} materiali
                  </div>
                ))}
              </div>
              <button
                onClick={importMaterialsFromJson}
                className="ay-mt-3 ay-px-4 ay-py-2 ay-bg-green-600 ay-text-white ay-rounded ay-hover:ay-bg-green-700"
              >
                Importa Materiali
              </button>
            </div>
          )}
        </div>
      )}

      {/* Products Import */}
      {activeSection === "products" && (
        <div className="ay-space-y-6">
          <div className="ay-bg-blue-50 ay-p-4 ay-rounded-lg">
            <h3 className="ay-font-medium ay-mb-2">Import Prodotti da CSV</h3>
            <p className="ay-text-sm ay-text-gray-600">
              Carica un file CSV con i prodotti. Potrai mappare le colonne ai
              campi necessari.
            </p>
          </div>

          <div>
            <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
              File CSV
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvFile}
              className="ay-block ay-w-full ay-text-sm ay-text-gray-500 ay-file:ay-mr-4 ay-file:ay-py-2 ay-file:ay-px-4 ay-file:ay-rounded-full ay-file:ay-border-0 ay-file:ay-text-sm ay-file:ay-font-semibold ay-file:ay-bg-blue-50 ay-file:ay-text-blue-700 ay-hover:ay-file:ay-bg-blue-100"
            />
          </div>

          {csvData && (
            <div className="ay-space-y-4">
              <h4 className="ay-font-medium">Mappa Colonne CSV</h4>

              <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 ay-gap-4">
                {Object.entries(fieldMapping).map(([field, value]) => (
                  <div key={field}>
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                      {field === "name" && "* "}
                      {field === "code" && "* "}
                      {field.charAt(0).toUpperCase() + field.slice(1)}
                    </label>
                    <select
                      value={value}
                      onChange={(e) =>
                        handleFieldMappingChange(field, e.target.value)
                      }
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm"
                    >
                      <option value="">-- Seleziona colonna --</option>
                      {csvHeaders.map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <h4 className="ay-font-medium">Valori Predefiniti</h4>
              <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 ay-gap-4">
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Categoria Default
                  </label>
                  <input
                    type="text"
                    value={defaultValues.category}
                    onChange={(e) =>
                      handleDefaultValueChange("category", e.target.value)
                    }
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                    placeholder="Senza Categoria"
                  />
                </div>
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Famiglia Default
                  </label>
                  <input
                    type="text"
                    value={defaultValues.family}
                    onChange={(e) =>
                      handleDefaultValueChange("family", e.target.value)
                    }
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                    placeholder="Senza Famiglia"
                  />
                </div>
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Prezzo Default
                  </label>
                  <input
                    type="number"
                    value={defaultValues.price}
                    onChange={(e) =>
                      handleDefaultValueChange("price", e.target.value)
                    }
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Tag Default
                  </label>
                  <input
                    type="text"
                    value={defaultValues.tag}
                    onChange={(e) =>
                      handleDefaultValueChange("tag", e.target.value)
                    }
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                  />
                </div>
              </div>

              <div className="ay-bg-yellow-50 ay-p-4 ay-rounded-lg">
                <p className="ay-text-sm ay-text-yellow-800">
                  Anteprima: {csvData.length} righe trovate nel CSV
                </p>
                <p className="ay-text-sm ay-text-yellow-800">
                  Campi obbligatori: Nome e Codice devono essere mappati
                </p>
              </div>

              <button
                onClick={importProductsFromCsv}
                disabled={!fieldMapping.name || !fieldMapping.code}
                className="ay-px-4 ay-py-2 ay-bg-blue-600 ay-text-white ay-rounded ay-hover:ay-bg-blue-700 ay-disabled:ay-bg-gray-400"
              >
                Importa Prodotti
              </button>
            </div>
          )}
        </div>
      )}

      {/* Export */}
      {activeSection === "export" && (
        <div className="ay-space-y-6">
          <div className="ay-bg-blue-50 ay-p-4 ay-rounded-lg">
            <h3 className="ay-font-medium ay-mb-2">Export Dati</h3>
            <p className="ay-text-sm ay-text-gray-600">
              Esporta i dati del configuratore per backup o condivisione.
            </p>
          </div>

          <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 lg:ay-grid-cols-3 ay-gap-4">
            {/* Export CSV Prodotti */}
            <div className="ay-border ay-border-gray-200 ay-rounded-lg ay-p-4">
              <h4 className="ay-font-medium ay-mb-2">Export Prodotti CSV</h4>
              <p className="ay-text-sm ay-text-gray-600 ay-mb-4">
                Esporta tutti i prodotti in formato CSV per fogli di calcolo.
              </p>
              <button
                onClick={exportProductsToCSV}
                className="ay-w-full ay-px-4 ay-py-2 ay-bg-green-600 ay-text-white ay-rounded ay-hover:ay-bg-green-700"
              >
                Scarica CSV Prodotti
              </button>
            </div>

            {/* NUOVO Export Completo JSON */}
            <div className="ay-border ay-border-gray-200 ay-rounded-lg ay-p-4">
              <h4 className="ay-font-medium ay-mb-2">Backup Completo JSON</h4>
              <p className="ay-text-sm ay-text-gray-600 ay-mb-4">
                Esporta l'intero products.json per backup completo del sistema.
              </p>
              <button
                onClick={exportCompleteProductsJSON}
                className="ay-w-full ay-px-4 ay-py-2 ay-bg-blue-600 ay-text-white ay-rounded ay-hover:ay-bg-blue-700"
              >
                Backup JSON Completo
              </button>
            </div>

            {/* Export Legacy */}
            <div className="ay-border ay-border-gray-200 ay-rounded-lg ay-p-4">
              <h4 className="ay-font-medium ay-mb-2">Backup Legacy</h4>
              <p className="ay-text-sm ay-text-gray-600 ay-mb-4">
                Backup dei soli dati prodotti (formato precedente).
              </p>
              <button
                onClick={() => {
                  const data = {
                    products: products,
                    timestamp: new Date().toISOString(),
                    version: "1.0",
                  }
                  const blob = new Blob([JSON.stringify(data, null, 2)], {
                    type: "application/json",
                  })
                  const url = URL.createObjectURL(blob)
                  const link = document.createElement("a")
                  link.href = url
                  link.download = `backup-legacy-${
                    new Date().toISOString().split("T")[0]
                  }.json`
                  link.click()
                  URL.revokeObjectURL(url)
                }}
                className="ay-w-full ay-px-4 ay-py-2 ay-bg-purple-600 ay-text-white ay-rounded ay-hover:ay-bg-purple-700"
              >
                Backup Legacy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImportExportTab
